import { FlowsResponse } from '../../types/FlowsTypes';
import {
  checkQuestionnaire,
  initFlowsSession,
  setFlowsResponse,
  updateAnswers,
} from '../../store/redux/slices/flowsSlice';
import { useFlowsDispatch } from '../../../../common/src/utils/globalVariables';
import { FlowFunctionParams } from './useFlowsInitNew';
import { cloneDeep } from 'lodash';
import { setQueryParam } from '../../../../common/src/hooks/query-params/useAddQueryParam';

interface UseFlowsStructureModifications {
  disableAllQuestions: (flowsResponse: FlowsResponse) => FlowsResponse;
  prepareFlowForInit: (params: FlowFunctionParams) => void;
}

/**
 * This hook contains functions that can modify the flow 1 way or another
 */
const useFlowsStructureModifications = (): UseFlowsStructureModifications => {
  const flowsDispatch = useFlowsDispatch();

  const disableAllQuestions = (flowsResponse: FlowsResponse) => {
    const flowsResponseCopy = cloneDeep(flowsResponse);

    flowsResponseCopy.questionnaire = {
      ...flowsResponseCopy.questionnaire,
      groups: flowsResponseCopy.questionnaire.groups.map((group) => ({
        ...group,
        questions: group.questions.map((question) => ({
          ...question,
          rules: {
            ...question.rules,
            is_disabled: true,
          },
        })),
      })),
    };

    return flowsResponseCopy;
  };

  /**
   * Makes certain structural modifications the questionnaire before showing it to the user
   */
  const prepareFlowForInit = ({ flow, initParams }: FlowFunctionParams) => {
    const getValidInputUrl = () => {
      if (!initParams.inputUrl) return undefined;
      if (initParams.inputUrl.includes('/offers/')) return initParams.inputUrl;
      return setQueryParam(initParams.inputUrl, {
        session_id: flow.session_id,
      });
    };

    flowsDispatch(
      initFlowsSession({
        flowId: initParams.flowId,
        inputUrl: getValidInputUrl(),
        outputUrl: setQueryParam(initParams.outputUrl, {
          session_id: flow.session_id,
        }),
        response: flow,
        shouldComplete: initParams.shouldComplete,
      }),
    );
    flowsDispatch(setFlowsResponse(flow));

    flow.questionnaire.groups.forEach(({ questions, id: groupId }) =>
      questions.forEach(({ id, value, index }) => {
        if (value != null)
          flowsDispatch(
            updateAnswers({
              id: id,
              answer: value,
              groupId,
              index,
              prefilled: true,
            }),
          );
      }),
    );

    flowsDispatch(checkQuestionnaire());
  };

  return {
    disableAllQuestions,
    prepareFlowForInit,
  };
};

export default useFlowsStructureModifications;
